import { CelestialArticlesPage } from "@local-cms/components/CelestialArticlesPage";
import { celestialArticles } from "@local-cms/data/celestialArticles";

import type { TenantConfig } from "../types";

import appleTouchIcon from "./favicon/apple-touch-icon.png";
import favicon from "./favicon/favicon.ico";
import icon192 from "./favicon/web-app-manifest-192x192.png";
import icon512 from "./favicon/web-app-manifest-512x512.png";
import { branding } from "./branding";
import build from "./build.json";
import celestialBackgroundImage from "./celestial-auth-background.png";
import celestialLogoMidnight from "./celestial-logo-midnight.svg";
import celestialLogoWhite from "./celestial-logo-white.svg";
import celestialSmallMidnight from "./celestial-small-c-midnight.svg";
import celestialSmallWhite from "./celestial-small-c-white.svg";

export const tenantConfig: TenantConfig = {
  metadata: {
    name: "Celestial",
    companyId: "ccc90110-cccc-cccc-cccc-c90110c90110",
    legalNames: {
      globalManagement: "Celestial Global Management",
      managementInternational: "Celestial Management International LLP",
    },
    locale: "en-US",
    defaultCurrency: "USD",
  },

  branding: {
    ...branding,
    assets: {
      auth: {
        backgroundImage: celestialBackgroundImage,
      },
    },

    logos: {
      logoDark: celestialLogoWhite,
      logoLight: celestialLogoMidnight,
      smallLogoDark: celestialSmallWhite,
      smallLogoLight: celestialSmallMidnight,
      auth: {
        logo: celestialLogoWhite,
        smallLogo: celestialSmallWhite,
      },
    },

    icons: {
      favicon,
      appleTouch: appleTouchIcon,
      icon192,
      icon512,
    },

    defaultMode: "light",
    borderRadius: "8px",
  },

  features: {
    use_shared_lp_home_page: true,
    reporting_homepage_carried_interest: true,
    reporting_capital_activity_page: true,
    reporting_settings_investments: true,
    reporting_settings_notifications_notification: true,
    reporting_documents_investment_tab: true,
    reporting_homepage_investment: true,
    reporting_homepage_recent_activity: true,
    reporting_homepage_registered_products: true,
    reporting_homepage_leverage_investments: true,
    reporting_homepage_loan_application: true,
    reporting_homepage_restricted_investments: true,
    reporting_homepage_pending_actions: true,
    reporting_homepage_waterfall_charts: true,
    reporting_gp_anaytics_user_capital_activity: true,
    reporting_gp_fund_investments_tab: true,
    reporting_gp_fund_carried_interest_tab: true,
    reporting_gp_fund_capital_activity_tab: true,
    reporting_gp_impersonation_notification_tab: true,
    reporting_gp_organizations_investments_column: true,
    reporting_gp_organizations_deepdive_investments_tab: true,
    reporting_gp_organizations_deepdive_loan_application: false,
    reporting_gp_settings_cas_tab: false,
    reporting_gp_settings_cas_management: false,
    analytics_gp_page: true,
    lp_offerings_skip_listing_page: false,
    lp_offerings_hide_empty_columns: false,
    lp_offerings_show_opportunity_name: false,
    lp_show_navbar: true,
    funds_tabs_disclosure_disclaimers: true,
    funds_investor_eligibility_field: true,
    funds_vintage_performance_gross_metrics: false,
    funds_hide_vehicle_switcher_single_option: false,
    subscription_non_employee_legal_entities_enabled: true,
    gp_sanity_cms_enabled: false, // Celestial uses local CMS, not Sanity
    auth_okta_login_flow: true,
    ingestion_import_data_funds: true,
    ingestion_import_data_organizations: true,
    ingestion_import_data_investing_legal_entities: true,
    ingestion_import_data_investments: true,
    ingestion_import_data_opportunities: true,
    ingestion_import_data_investor_documents: true,
    ingestion_import_data_investment_metrics: true,
    ingestion_import_data_users: true,
    ingestion_import_data_user_roles: true,
    ingestion_import_data_capital_activity: true,
    ingestion_import_data_capital_activity_cash_flow: true,
    ingestion_import_data_wire_instruction: true,
    ingestion_id_manager_funds: true,
    ingestion_id_manager_organizations: true,
    ingestion_id_manager_investing_legal_entities: true,
    ingestion_id_manager_investments: true,
    ingestion_id_manager_opportunities: true,
    ingestion_id_manager_investor_documents: true,
    ingestion_id_manager_wire_instructions: true,
    ingestion_id_manager_users: true,
    ingestion_id_manager_capital_activities: true,
    ingestion_id_manager_investment_metrics: true,
    ingestion_id_manager_user_roles: true,
    gp_parent_organization_redirect_to_investments: true,
    gp_co_investment_page: true,
    gp_cases_page: true,
    gp_entity_relationship_graph_page: true,
    gp_workbench_changelog_tab: true,
    gp_fund_opportunities_page_v2: true,
    gp_opportunity_management_edit_enabled: true,
    gp_opportunity_multi_invite_enabled: true,
    gp_co_investment_update_salesforce_data: false,
    lp_home_performance_investment_level: true,
    branding_show_powered_by_vega: true,
  },

  contacts: {
    employeeEmail: "product@vega.investments",
    investorEmail: "product@vega.investments",
  },

  links: {
    privacyPolicy: "https://www.vega-alts.com/privacy-policy",
    footer: [
      {
        title: "Privacy Policy",
        path: "https://www.vega-alts.com/privacy-policy",
      },
      {
        title: "Terms of use",
        path: "https://www.vega-alts.com/",
      },
      {
        title: "Security Policy",
        path: "https://trust.vega-alts.com/",
      },
    ],
    registration: [
      {
        title: "Security Policy",
        path: "https://trust.vega-alts.com/",
      },
      {
        title: "Privacy Policy",
        path: "https://www.vega-alts.com/privacy-policy",
      },
      {
        title: "Terms of use",
        path: "https://www.vega-alts.com/",
      },
    ],
  },

  content: {
    tabTitles: build.portals,
    footerText:
      "© Celestial Global Management, Inc. {{year}} All Rights Reserved.",
    welcomeInvestments: [
      {
        title: "Browse through open investment opportunities",
        description: "Explore available Celestial funds for future investments",
        logo: "/images/celestial-welcome-0.png",
      },
      {
        title: "Subscribe digitally to funds directly on the platform",
        description: "Request, complete and manage the subscription digitally",
        logo: "/images/celestial-welcome-1.png",
      },
      {
        title: "Capital calls, distributions and reports in one place",
        description:
          "Facilitate your alternative asset operations with our LP platform",
        logo: "/images/celestial-welcome-2.png",
      },
    ],
    registeredProducts: [],
  },

  cms: {
    provider: "local",
    resourcesPageComponent: CelestialArticlesPage,
    articles: celestialArticles,
  },
} as const;
